import {
  EmptyState,
  FlexBox,
  SelectionList,
  Spinner,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { HookFormSelectionList } from 'lib-shared-hook-form';
import { PaginatedApiArgs, PaginatedApiResponse } from 'lib-shared-types-api';
import identity from 'lodash/identity';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useDebounce, useMount, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';

import styles from './hookFormSearchHitSelect.module.scss';
import {
  HookFormSearchHit,
  HookFormSearchHitSelectProps,
} from './hookFormSearchHitSelect.types';

const defaultGetSuggestions = () => [];

export const HookFormSearchHitSelect = <
  TPaginatedApiArg extends PaginatedApiArgs,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TSearchHit extends HookFormSearchHit,
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  endpoint,
  transformValue = identity,
  renderSelectionListItemContent,
  componentProps,
  getSuggestions = defaultGetSuggestions,
  ...fieldProps
}: HookFormSearchHitSelectProps<
  TPaginatedApiArg,
  TPaginatedApiResponse,
  TSearchHit,
  TFieldValues,
  TFieldName
>): JSX.Element => {
  const { lazy = true } = endpoint;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const {
    items: hits,
    asyncStatus,
    changeArg,
    search,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({ ...endpoint.options, infinite: true });

  useMount(() => {
    if (!lazy) {
      changeArg(endpoint.initialArg, { debounceTime: 0 });
    }
  });

  useUpdateEffect(() => {
    search(debouncedSearchTerm, { debounceTime: 0 });
  }, [search, debouncedSearchTerm]);

  const handleToggleDropdown = useCallback(
    (newIsOpen: boolean): void => {
      // If the dropdown is closed, reset the search term
      if (!newIsOpen) {
        setSearchTerm('');
      }

      // If the dropdown is opened and lazy loading is enabled, load the initial list
      if (lazy && newIsOpen && !hits.length) {
        changeArg(endpoint.initialArg, { debounceTime: 0 });
      }
    },
    [changeArg, endpoint.initialArg, lazy, hits.length],
  );

  const inputSelectionListProps = useMemo(
    (): ComponentProps<
      typeof HookFormSelectionList<TFieldValues[TFieldName]>
    >['componentProps'] => ({
      ...componentProps,
      dropdownProps: {
        ...componentProps?.dropdownProps,
        className: classNames(
          componentProps?.dropdownProps?.className,
          styles.dropdown,
        ),
      },
      filter: {
        value: searchTerm,
        onChange: (e) => setSearchTerm(e.target.value),
      },
      getItemTitleByValue: (value) =>
        componentProps?.getItemTitleByValue?.(value) ?? '',
      onToggleDropdown: handleToggleDropdown,
    }),
    [componentProps, handleToggleDropdown, searchTerm],
  );

  const computedHits = useMemo(
    () => [...getSuggestions(debouncedSearchTerm, hits), ...hits],
    [getSuggestions, debouncedSearchTerm, hits],
  );

  return (
    <HookFormSelectionList
      {...fieldProps}
      componentProps={inputSelectionListProps}
    >
      <>
        {computedHits.map((hit) => (
          <SelectionList.Item
            key={hit.id}
            value={transformValue(hit)}
            className={styles.item}
          >
            {renderSelectionListItemContent?.(hit) ?? hit.id}
          </SelectionList.Item>
        ))}

        {asyncStatus === 'pending' && (
          <FlexBox as="li" padding="300" justifyContent="center">
            <Spinner size={20} />
          </FlexBox>
        )}

        {asyncStatus === 'fulfilled' && !computedHits.length && (
          <FlexBox as="li" padding="300" justifyContent="center">
            <EmptyState
              title="No results found"
              description="Try changing your search criteria."
            />
          </FlexBox>
        )}

        {asyncStatus === 'fulfilled' && hasNextPage && (
          <Waypoint onEnter={nextPage} />
        )}
      </>
    </HookFormSelectionList>
  );
};
